import { useEffect } from 'react'
import {
  ChatProvider,
  ChatRoot,
  ChatHeader,
  ChatMessageList,
  ChatConnectionStatus,
  ChatFloatingWrapper,
  useChatContext,
  registerCustomElement,
} from '@almai1/chatbot-widget'
import { useTranslation } from 'react-i18next'
import { LeadForm, SuggestionButtons, useLocaleSync } from '../cards'
import { DeerIcon } from './components/DeerIcon'
import { RestartButton } from './components/RestartButton'
import { TypingIndicator } from './components/TypingIndicator'
import { SuggestionButtonsBar } from './components/SuggestionButtonsBar'
import { ChatInputWithError } from './components/ChatInputWithError'
import { EmptyState } from './components/EmptyState'

// Wrapper for LeadForm that injects context values
const LeadFormWrapper: React.FC<{ data?: unknown }> = ({ data }) => {
  const { sessionId, sessionKey, config, handleSseEvent, locale } = useChatContext()
  // Extract language code from locale (e.g., 'it-IT' -> 'it')
  const language = locale?.split('-')[0] || 'en'
  return (
    <LeadForm
      data={data}
      apiBaseUrl={config.apiBaseUrl}
      sessionId={sessionId}
      sessionKey={sessionKey}
      onSseEvent={handleSseEvent}
      language={language}
    />
  )
}

// Wrapper for SuggestionButtons that injects sendMessage
const SuggestionButtonsWrapper: React.FC<{ data?: unknown }> = ({ data }) => {
  const { sendMessage } = useChatContext()
  return (
    <SuggestionButtons
      data={data as { buttons: { text: string; action: string }[] }}
      onSendMessage={sendMessage}
    />
  )
}

// Register custom elements
registerCustomElement('lead_form', LeadFormWrapper)
registerCustomElement('suggestion_buttons', SuggestionButtonsWrapper)

// Default API URL (can be overridden via prop)
const DEFAULT_API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:50040/api'

export type WidgetMode = 'embedded' | 'popup'

function LocaleSync() {
  const { locale } = useChatContext()
  useLocaleSync(locale)
  return null
}

function CloseButton() {
  const { setIsOpen } = useChatContext()
  const { t } = useTranslation()

  return (
    <button
      className="widget-close-btn"
      onClick={() => setIsOpen(false)}
      title={t('widget.closeButton', 'Close')}
      aria-label={t('widget.closeButton', 'Close')}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6L6 18" />
        <path d="M6 6l12 12" />
      </svg>
    </button>
  )
}

function ChatContent({ mode = 'embedded' }: { mode?: WidgetMode }) {
  const { t } = useTranslation()
  const { setIsOpen } = useChatContext()

  // Close popup on Escape key
  useEffect(() => {
    if (mode !== 'popup') return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [mode, setIsOpen])

  return (
    <ChatRoot className="widget-root">
      <ChatHeader className="widget-header">
        <div className="widget-header-content">
          <div className="widget-header-logo">
            <DeerIcon />
          </div>
          <div className="widget-header-text">
            <h1>{t('widget.headerTitle')}</h1>
            <p>{t('widget.headerSubtitle')}</p>
          </div>
        </div>
        <div className="widget-header-actions">
          <RestartButton />
          {mode === 'popup' && <CloseButton />}
          <ChatConnectionStatus
            className="widget-connection-status"
            showWhenReady={false}
            noSessionText={t('widget.offline')}
          />
        </div>
      </ChatHeader>

      <ChatMessageList
        className="widget-messages"
        emptyState={<EmptyState />}
      />

      <TypingIndicator />

      <SuggestionButtonsBar />
      <ChatInputWithError />
    </ChatRoot>
  )
}

function PopupLauncherIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 4l-2 2M6 4l2 2"/>
      <path d="M12 8c-2.5 0-4.5 2-4.5 4.5V16c0 2.5 2 4 4.5 4s4.5-1.5 4.5-4v-3.5C16.5 10 14.5 8 12 8z"/>
      <path d="M9 15h.01M15 15h.01"/>
      <path d="M16 4c1.5 1.5 2 3 2 4M8 4c-1.5 1.5-2 3-2 4"/>
    </svg>
  )
}

function ChatWidgetInner({ mode = 'embedded' }: { mode?: WidgetMode }) {
  if (mode === 'popup') {
    return (
      <>
        <LocaleSync />
        <ChatFloatingWrapper
          position="bottom-right"
          offset={{ x: 20, y: 20 }}
          launcherClassName="widget-popup-launcher"
          panelClassName="widget-popup-panel"
          closeButtonClassName="widget-popup-close"
          showCloseButton={false}
          launcherContent={<PopupLauncherIcon />}
        >
          <ChatContent mode="popup" />
        </ChatFloatingWrapper>
      </>
    )
  }

  return (
    <>
      <LocaleSync />
      <ChatContent mode="embedded" />
    </>
  )
}

export interface ChatWidgetProps {
  apiUrl?: string
  sessionId?: string  // For loading existing sessions
  locale?: string     // Initial locale (e.g., 'en', 'it')
  mode?: WidgetMode
}

export function ChatWidget({ apiUrl, sessionId, locale, mode = 'embedded' }: ChatWidgetProps) {
  const { t, i18n } = useTranslation()
  const baseUrl = apiUrl || DEFAULT_API_URL

  // Set initial locale if provided
  if (locale && i18n.language !== locale) {
    i18n.changeLanguage(locale)
  }

  return (
    <ChatProvider
      config={{
        apiBaseUrl: baseUrl,
        sessionId: sessionId,
        persistSession: !sessionId,  // Don't persist if viewing existing session
        sessionTTL: 8,
        enableAudio: true,
        showTimestamps: true,
        showToolStatus: true,
        placeholder: t('widget.inputPlaceholder'),
      }}
    >
      <ChatWidgetInner mode={mode} />
    </ChatProvider>
  )
}
